import { useCallback, useEffect, useState } from "react";
import { AlertCircle, Lock, MessageSquareText, RefreshCw, ThumbsUp } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

export interface AdminResearchFeedback {
  id: number; investorId: number; pimId: number; versionId: number;
  useful: boolean | null; why: string | null; submittedAt: string;
  notificationStatus: string; notificationAttemptedAt: string | null; notificationSentAt: string | null;
  notificationProviderId: string | null; notificationError: string | null;
  investorName?: string | null; investorEmail?: string | null;
}

interface InvestorLite { id: number; name?: string | null; fullName?: string | null; email?: string | null }

function headers(): Record<string, string> {
  return { "Content-Type": "application/json", "x-admin-token": localStorage.getItem("admin_token") || "" };
}

async function readError(r: Response, fallback: string) {
  try { const j = await r.json() as { error?: { message?: string } | string }; if (typeof j.error === "string") return j.error; if (j.error?.message) return j.error.message; } catch { /* non-JSON */ }
  return fallback;
}

const fmt = (v: string | null) => (v ? new Date(v).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

function statusTone(s: string) {
  if (s === "sent") return "border-emerald-600/30 text-emerald-800 dark:text-emerald-300";
  if (s === "failed") return "border-destructive/40 text-destructive";
  return "border-amber-600/30 text-amber-800 dark:text-amber-300";
}

export function AdminResearchFeedbackSection({ pimId }: { pimId: number }) {
  const [items, setItems] = useState<AdminResearchFeedback[] | null>(null);
  const [investors, setInvestors] = useState<Map<number, InvestorLite>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<number | null>(null);
  const [rowError, setRowError] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE}api/admin/research-pipeline/pims/${pimId}/feedback`, { headers: headers(), cache: "no-store" });
      if (!r.ok) throw new Error(await readError(r, "Feedback could not be loaded."));
      const j = await r.json() as { data: AdminResearchFeedback[] };
      const list = Array.isArray(j.data) ? j.data : [];
      setItems(list);
      if (list.some(f => !f.investorName)) {
        try {
          const ri = await fetch(`${BASE}api/admin/investors`, { headers: headers() });
          if (ri.ok) {
            const raw = await ri.json() as InvestorLite[] | { data: InvestorLite[] };
            const arr = Array.isArray(raw) ? raw : Array.isArray(raw.data) ? raw.data : [];
            setInvestors(new Map(arr.map(i => [i.id, i])));
          }
        } catch { /* identity falls back to ID */ }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Feedback could not be loaded.");
    } finally { setLoading(false); }
  }, [pimId]);

  useEffect(() => { if (pimId) void load(); }, [load, pimId]);

  const retry = async (f: AdminResearchFeedback) => {
    setRetrying(f.id); setRowError(e => ({ ...e, [f.id]: "" }));
    try {
      const r = await fetch(`${BASE}api/admin/research-pipeline/pims/${pimId}/feedback/${f.id}/retry`, { method: "POST", headers: headers() });
      if (!r.ok) throw new Error(await readError(r, "Retry failed."));
      const j = await r.json() as { data?: Partial<AdminResearchFeedback> & { status?: string } };
      const d = j.data ?? {};
      setItems(list => list?.map(x => x.id === f.id ? { ...x, ...d, notificationStatus: d.notificationStatus ?? d.status ?? x.notificationStatus } : x) ?? list);
    } catch (e) {
      setRowError(er => ({ ...er, [f.id]: e instanceof Error ? e.message : "Retry failed." }));
    } finally { setRetrying(null); }
  };

  const identity = (f: AdminResearchFeedback) => {
    const inv = investors.get(f.investorId);
    const name = f.investorName || inv?.name || inv?.fullName;
    const email = f.investorEmail || inv?.email;
    return { name: name || `Investor #${f.investorId}`, email };
  };

  return (
    <section aria-labelledby="admin-feedback-title" data-testid="section-admin-research-feedback" className="rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="admin-feedback-title" className="flex items-center gap-2 font-serif text-xl"><MessageSquareText className="h-5 w-5 text-primary" aria-hidden="true" /> Investor feedback</h2>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" aria-hidden="true" /> Private to the investor and administrators. Never share with the company.</p>
        </div>
        <button type="button" onClick={() => void load()} data-testid="button-refresh-feedback" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" /> Refresh
        </button>
      </div>

      {loading && !items ? (
        <div role="status" data-testid="status-admin-feedback-loading" className="mt-5 space-y-3">
          {[0, 1].map(i => <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />)}
          <span className="sr-only">Loading feedback</span>
        </div>
      ) : error ? (
        <div role="alert" data-testid="status-admin-feedback-error" className="research-error-surface mt-5 rounded-lg border p-4 text-sm">
          <div className="flex items-start gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div><p>{error}</p><button type="button" onClick={() => void load()} data-testid="button-retry-admin-feedback" className="mt-2 text-xs font-semibold underline underline-offset-4">Try again</button></div>
          </div>
        </div>
      ) : !items?.length ? (
        <p data-testid="status-admin-feedback-empty" className="mt-5 rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No investor feedback yet for this memorandum.</p>
      ) : (
        <ul className="mt-5 divide-y divide-border" data-testid="list-admin-feedback">
          {items.map(f => {
            const who = identity(f);
            const canRetry = f.notificationStatus === "failed" || f.notificationStatus === "pending";
            return (
              <li key={f.id} data-testid={`row-admin-feedback-${f.id}`} className="py-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold" data-testid={`text-feedback-investor-${f.id}`}>{who.name}</p>
                    {who.email && <p className="text-xs text-muted-foreground">{who.email}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {f.useful && <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 px-2 py-0.5 text-primary"><ThumbsUp className="h-3 w-3" aria-hidden="true" /> Useful</span>}
                    <span className="font-mono text-muted-foreground">v{f.versionId} · {fmt(f.submittedAt)}</span>
                  </div>
                </div>
                {f.why && <p className="mt-2 whitespace-pre-line break-words text-sm leading-6" data-testid={`text-feedback-why-${f.id}`}>{f.why}</p>}
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs">
                  <span data-testid={`status-feedback-notification-${f.id}`} className={`rounded-full border px-2 py-0.5 font-mono uppercase tracking-wider ${statusTone(f.notificationStatus)}`}>Notification {f.notificationStatus}</span>
                  <span className="text-muted-foreground">{f.notificationSentAt ? `Sent ${fmt(f.notificationSentAt)}` : f.notificationAttemptedAt ? `Attempted ${fmt(f.notificationAttemptedAt)}` : "Not attempted"}</span>
                  {canRetry && (
                    <button type="button" onClick={() => void retry(f)} disabled={retrying === f.id} data-testid={`button-retry-notification-${f.id}`} className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 font-medium hover:bg-muted disabled:opacity-60">
                      <RefreshCw className={`h-3 w-3 ${retrying === f.id ? "animate-spin" : ""}`} aria-hidden="true" /> Retry notification
                    </button>
                  )}
                </div>
                {f.notificationError && <p className="mt-1 text-xs text-destructive">{f.notificationError}</p>}
                {rowError[f.id] && <p role="alert" className="mt-1 text-xs text-destructive">{rowError[f.id]}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
